// useSsoSignIn — THE ONE OWNER of "sign in with Google / Apple" (s95 sign-up rebuild).
//
// SignInScreen and SignUpScreen each carried a ~150-line copy of this flow, identical
// down to the error keys. The sign-up rebuild puts the buttons on the Welcome screen
// and adds one server answer both copies would have had to learn:
//   · 403 needs2faCode  — ★ s98: ONLY when a Google/Apple identity is being linked, by
//     email, onto an existing account that has two-factor (Dr. Rami, 2026-10-07: Apple/
//     Google never get 2FA, except that one first link). The server returns an
//     `ssoChallenge`; the patient enters the code (or a backup code) and the second
//     call carries the challenge, never the provider token again (a Google web
//     sign-in can arrive as a one-time auth code that cannot be replayed).
// A 200 with a token is a session. s98 retired the 403 needs2faSetup answer (the server's
// SSO_REQUIRE_2FA switch is gone): an Apple/Google account sets a PIN, never 2FA.
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import { SocialLogin } from '@capgo/capacitor-social-login'
import { useUserStore } from '../stores'
import { authAPI } from '../services/api'
import { useI18n } from '../i18n'
import { isSmsRefused, smsRefusedSentence } from '../utils/smsRefusal'
import { oneTapEnded, preloadProviderSdk } from '../utils/ssoReauth'

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || ''

let _socialInited = false
async function ensureSocialInit() {
  if (_socialInited) return
  await SocialLogin.initialize({
    google: {
      webClientId: '643624439824-l6h1btsuoj3a5n0k28607bbrjr2c32ed.apps.googleusercontent.com',
      iOSClientId: '643624439824-gagojv418nvr2sefqe87a04m542bde4j.apps.googleusercontent.com',
      iOSServerClientId: '643624439824-l6h1btsuoj3a5n0k28607bbrjr2c32ed.apps.googleusercontent.com',
    },
    apple: {},
  })
  _socialInited = true
}

/** What a social-login answer means. Pure, so the gate script can hold it. */
export function ssoOutcome(response, err) {
  if (response && response.token && response.user) return { kind: 'session', user: response.user }
  const e = err || {}
  if (e.status === 403 && e.needs2faCode && e.ssoChallenge) {
    // PROD-807: smsRefused:'sms_us_ca_only' + smsRefusedMessage = the number is not US/Canada,
    // nothing was texted; the sheet shows the server's sentence instead of "We texted...".
    const refused = isSmsRefused(e)
    return { kind: 'code', challenge: e.ssoChallenge, method: e.method || 'totp', smsSent: !!e.smsSent && !refused, smsRefused: refused, smsRefusedMessage: refused ? smsRefusedSentence(e) : null }
  }
  return { kind: 'error', message: e.error || null }
}

export default function useSsoSignIn({ mode = 'signin' } = {}) {
  const navigate = useNavigate()
  const { t } = useI18n()
  const setUser = useUserStore((s) => s.setUser)
  const [ssoLoading, setSsoLoading] = useState(null) // 'google' | 'apple' | null
  const [ssoError, setSsoError] = useState(null)
  const [codeStep, setCodeStep] = useState(null) // { challenge, method, smsSent, provider } | null
  const [codeBusy, setCodeBusy] = useState(false)
  const [codeError, setCodeError] = useState(null)

  // The web Google SDK, loaded once for any screen that shows the buttons.
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || Capacitor.isNativePlatform()) return
    if (document.getElementById('google-gsi-script')) return
    const script = document.createElement('script')
    script.id = 'google-gsi-script'
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    document.head.appendChild(script)
  }, [])
  // PROD-1159: the Apple SDK too, so the tap reaches AppleID.auth.signIn() with no await in
  // front of it (a popup opened after an await can be blocked silently).
  useEffect(() => { preloadProviderSdk('apple') }, [])

  const failText = (provider) => (mode === 'signup'
    ? t('auth.signingUpWith', { provider: provider === 'google' ? 'Google' : 'Apple' })
    : t('auth.signingInWith', { provider: provider === 'google' ? 'Google' : 'Apple' }))

  const settle = useCallback((outcome, provider) => {
    if (outcome.kind === 'session') {
      // PROD-943: remember WHICH provider opened this session, so a later
      // re-authentication (account deletion) offers that one first.
      setUser({ ...outcome.user, signInProvider: provider }, { freshSignIn: true })
      navigate('/home', { replace: true })
      return
    }
    if (outcome.kind === 'code') {
      setCodeError(outcome.smsRefused ? outcome.smsRefusedMessage : null)
      setCodeStep({ challenge: outcome.challenge, method: outcome.method, smsSent: outcome.smsSent, smsRefused: outcome.smsRefused, provider })
      return
    }
    setSsoError(outcome.message || failText(provider))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, setUser])

  const completeSocialLogin = useCallback(async (provider, token, firstName, lastName) => {
    setSsoLoading(provider)
    setSsoError(null)
    try {
      const response = await authAPI.socialLogin(provider, token, firstName, lastName)
      settle(ssoOutcome(response, null), provider)
    } catch (err) {
      console.error(`[SSO_${provider}] Error:`, err)
      settle(ssoOutcome(null, err), provider)
    } finally {
      setSsoLoading(null)
    }
  }, [settle])

  const submitCode = useCallback(async (value, { backup = false } = {}) => {
    if (!codeStep) return
    setCodeBusy(true)
    setCodeError(null)
    try {
      const response = await authAPI.socialLoginChallenge(codeStep.challenge, backup ? { backupCode: value } : { code: value })
      if (response && response.token && response.user) {
        setCodeStep(null)
        setUser({ ...response.user, signInProvider: codeStep.provider }, { freshSignIn: true })
        navigate('/home', { replace: true })
      } else {
        setCodeError(t('auth.invalidCode'))
      }
    } catch (err) {
      if (err && err.errorType === 'sso_challenge_expired') {
        setCodeStep(null)
        setSsoError(err.error || t('auth.ssoChallengeExpired', 'This sign-in expired. Please start again.'))
      } else {
        setCodeError((err && err.error) || t('auth.invalidCode'))
      }
    } finally {
      setCodeBusy(false)
    }
  }, [codeStep, navigate, setUser, t])

  const cancelCode = useCallback(() => { setCodeStep(null); setCodeError(null) }, [])

  const startGoogle = useCallback(() => {
    if (Capacitor.isNativePlatform()) {
      (async () => {
        setSsoError(null); setSsoLoading('google')
        try {
          await ensureSocialInit()
          try { await SocialLogin.logout({ provider: 'google' }) } catch (e) { /* not signed in */ }
          const res = await SocialLogin.login({ provider: 'google', options: { scopes: ['email', 'profile'] } })
          const r = res?.result || {}
          if (!r.idToken) { setSsoLoading(null); return }
          await completeSocialLogin('google', r.idToken, r.profile?.givenName || '', r.profile?.familyName || '')
        } catch (err) { console.error('[SSO_GOOGLE native]', err); setSsoLoading(null) }
      })()
      return
    }
    if (!GOOGLE_CLIENT_ID || !window.google?.accounts?.id) {
      setSsoError(t('errors2.googleLoading'))
      return
    }
    setSsoError(null)
    setSsoLoading('google')
    // PROD-1159 (review fb30d218 H1/H2): sign-in has NO time limit. A patient may spend minutes
    // in the provider's sheet (password, 2FA), and a limit would drop a sign-in that succeeds.
    // The spinner ends only when the provider has DEFINITELY finished without a result: a One
    // Tap the SDK cancelled, a code popup that answers with no code, or an explicit error.
    let answered = false
    try {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (response) => {
          answered = true
          if (response.credential) { completeSocialLogin('google', response.credential) }
          else { setSsoLoading(null); setSsoError(t('errors2.googleCancelled')) }
        },
        auto_select: false,
        cancel_on_tap_outside: true,
      })
      window.google.accounts.id.prompt((notification) => {
        const ended = oneTapEnded(notification)
        // PROD-1159: One Tap cancelled with no credential (oneTapEnded: 'cancel_called' only):
        // nothing more is coming from it, so the spinner ends.
        if (ended === 'dismissed') { if (!answered) setSsoLoading(null); return }
        if (ended) {
          setSsoLoading(null)
          try {
            const tokenClient = window.google.accounts.oauth2.initCodeClient({
              client_id: GOOGLE_CLIENT_ID,
              scope: 'openid email profile',
              ux_mode: 'popup',
              callback: (response) => {
                if (response.code) completeSocialLogin('google', response.code)
                else { setSsoLoading(null); setSsoError(t('errors2.googleCancelled')) }
              },
              error_callback: () => { setSsoLoading(null); setSsoError(t('errors2.googleFailed')) },
            })
            setSsoLoading('google')
            tokenClient.requestCode()
          } catch (fallbackErr) {
            console.error('[SSO_GOOGLE] Popup fallback error:', fallbackErr)
            setSsoLoading(null)
            setSsoError(t('errors2.googleFailed'))
          }
        }
      })
    } catch (err) {
      console.error('[SSO_GOOGLE] Init error:', err)
      setSsoLoading(null)
      setSsoError(t('errors2.googleFailed'))
    }
  }, [completeSocialLogin, t])

  const startApple = useCallback(async () => {
    if (Capacitor.isNativePlatform()) {
      setSsoError(null); setSsoLoading('apple')
      try {
        await ensureSocialInit()
        const res = await SocialLogin.login({ provider: 'apple', options: { scopes: ['email', 'name'] } })
        const r = res?.result || {}
        if (!r.idToken) { setSsoLoading(null); return }
        await completeSocialLogin('apple', r.idToken, r.profile?.givenName || '', r.profile?.familyName || '')
      } catch (err) { console.error('[SSO_APPLE native]', err); setSsoLoading(null) }
      return
    }
    setSsoError(null)
    setSsoLoading('apple')
    try {
      if (!window.AppleID) {
        await new Promise((resolve, reject) => {
          if (document.getElementById('apple-signin-script')) {
            const check = setInterval(() => { if (window.AppleID) { clearInterval(check); resolve() } }, 100)
            setTimeout(() => { clearInterval(check); reject(new Error('Apple SDK timeout')) }, 5000)
            return
          }
          const script = document.createElement('script')
          script.id = 'apple-signin-script'
          script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'
          script.onload = () => {
            const check = setInterval(() => { if (window.AppleID) { clearInterval(check); resolve() } }, 100)
            setTimeout(() => { clearInterval(check); reject(new Error('Apple SDK timeout')) }, 5000)
          }
          script.onerror = () => reject(new Error('Failed to load Apple SDK'))
          document.head.appendChild(script)
        })
      }
      window.AppleID.auth.init({
        clientId: 'com.orellahealth.signin',
        scope: 'name email',
        redirectURI: window.location.origin + (mode === 'signup' ? '/signup' : '/signin'),
        usePopup: true,
      })
      const response = await window.AppleID.auth.signIn()
      const idToken = response?.authorization?.id_token
      if (!idToken) { setSsoLoading(null); setSsoError(t('errors2.appleSignInCancelled')); return }
      await completeSocialLogin('apple', idToken, response?.user?.name?.firstName || '', response?.user?.name?.lastName || '')
    } catch (err) {
      console.error('[SSO_APPLE] Error:', err)
      setSsoLoading(null)
      if (err?.error === 'popup_closed_by_user' || err?.message?.includes('popup')) return
      setSsoError(t('errors2.appleSignInFailed'))
    }
  }, [completeSocialLogin, mode, t])

  return { ssoLoading, ssoError, setSsoError, startGoogle, startApple, codeStep, codeBusy, codeError, submitCode, cancelCode }
}
